import { renderToStaticMarkup } from "react-dom/server";
import type BaseModel from "../../../src/models/BaseModel";
import { C1_OrangeSelectionFilter } from "./C1_OrangeSelectionFilter";
import { MOBILE_SELECTED_ELO_MOVE, type MobileEloMoveRequest } from "./C1_mobileSelectedMove";
import { eloSelectionOpacity, eloSelectionHoverHandlers, independentEloOwner } from "./C1_eloSelectionFocus";
import { C1_DinSymbol } from "./C1_DinSymbol";
import { C1_ELO_ITEM_TYPE_LABELS } from "../C1_uiLabels";
import { itx } from "../../../src/translationItx/itx";
import { resolvePlanDisplayMode } from "./C1_editModePresentation";
import { useDelayedClick } from "../../../src/utils/useDelayedClick";
import { ConnectionEndpointGroup } from "../../../src/connectionAnalyser/ConnectionEndpointGroup";
import { C1_PlanControlPortal } from "./C1_PlanControlPortal";
import C1_EloItem from "../model/C1_EloItem";
import { C1_Furniture } from "../model/C1_Furniture";
import { C1_FurnitureUprightContent } from "../furniture/C1_FurnitureSvg";
import { detachEloItemWithToast } from "./C1_detachEloItemWithToast";
import { moveInteractiveEloPlacement, interactiveEloAbsolutePosition } from "../model/C1_interactiveEloPlacement";
import { C1_Button } from "../model/C1_Button";
import { C1_Consumer } from "../model/C1_Consumer";
import { getChannelTypePicture } from "../../../src/models/ChannelType";
import { getC1EloDefinitionIconUrl } from "../furniture/C1_EloDefinitionIcons";
import { C1_EloGroup } from "../model/C1_EloGroup";
import c1_state from "../model/C1_State";
import { C1_EventKey } from "../C1_EventKey";
import { isUiModeDeveloper, uiModeWatcher } from "../../../src/uiMode";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { getContrastTextColor } from "../../../src/modelViews/GraficElementUtils";
import { C1_TagShape } from "./C1_TagShape";
import { C1_EloCollisionFootprint } from "./C1_EloCollisionFootprint";
import { lightGroupPlanInstances } from "./C1_lightGroupPlanInstances";
import { C1_LightTagFunction } from "./C1_LightTagFunction";
import { C1_PlugTagFunction } from "./C1_PlugTagFunction";
import { C1_TagFunction } from "./C1_TagFunction";
import { eloItemScaleTransform, normalizeEloItemScale } from "./C1_eloItemScale";
import { C1_TAG_RADIUS, PLAN_VIEW_ANCHORS, tagPlacementOffset } from "./C1_tagPlacement";
import type { C1_DisplayMode, WallSide } from "../types/C1_ModelTypes";
import { eloWallCollisionDirection, resolveEloDisplayCollisions, type DisplayCollisionItem, type DisplayFootprint } from "./C1_eloDisplayCollision";

import {
  getLightProductImageUrl,
  getLightSymbolOutlineRadiusRatio,
} from "../data/C1_ProductDefinitions";
import {
  ChannelType,
  C1_EloItem_PlugType,
  C1_EloItem_Type,
  type C1_EloItem_Definition,
  type C1_EloItemPlanView,
  type C1_EloItemViewAnchor,
  type C1_LegacyEloItemTreeData,
} from "../types/C1_EloTypes";
import { WallOutletSvg, wallOutletPreviewBounds, type WallOutletSlotType } from "../frontViews/WallOutletFrontview";
import { useEffect, useRef, useState } from "react";
import { clientToPlan } from "./C1_planCoordinates";
import { PLAN_CANCEL_OBJECT_DRAG } from "./C1_planObjectPan";
import { cachedLightImageHitRect, loadLightImageHitRect } from "./C1_LightImageHitRectCache";
import temperatureIconUrl from "../data/others/temperature.svg";
import motionSensorIconUrl from "../data/others/motion-sensor.svg";
import motionSensorSvg from "../data/others/motion-sensor.svg?raw";
import presenceSensorIconUrl from "../data/others/presence-sensor.svg";
import humiditySensorIconUrl from "../data/others/humidity-sensor.svg";
import co2SensorIconUrl from "../data/others/co2-sensor.svg";
import brightnessSensorIconUrl from "../data/others/brightness-sensor.svg";
import smokeSensorIconUrl from "../data/others/smoke-sensor.svg";
import smokeSensorSvg from "../data/others/smoke-sensor.svg?raw";
import cameraSvg from "../data/others/camera.svg?raw";
import { FixedPowerFrontView } from "../frontViews/FixedPowerFrontView";
import { CameraFrontView } from "../frontViews/CameraFrontView";
import { contrastFactor, useProcessedEloGraphicUrls } from "./C1_EloGraphicContrast";
import { C1_ButtonView } from "./C1_ButtonView";
import { getC1ButtonDevice } from "../frontViews/C1_buttonRows";
// import { C1_ButtonPinBadge } from "./C1_ButtonPinBadge";
import { C1_EloInlineEditor } from "./C1_EloInlineEditor";
import { C1_EloAddBadge } from "./C1_EloAddBadge";
import { createEloGhostItems, type C1_EloGhostAction } from "../model/C1_eloGhostItems";
import { appendEloItem, appendRepeatedItem, repeatItemCount, repeatItemLimit } from "../model/C1_eloTreeEditing";
import { commitOwnerEloTree, eloRootAndOwner, materializedRootForEloOwner } from "../model/C1_eloOwnerEditing";
import { C1_ELO_PLAN_MODULE_SIZE } from "../lib/C1_planSymbolSizes";
import type { C1_BaseObject } from "../model/C1_BaseObject";
import { useTooltipController } from "../../../src/components/tooltip/TooltipHost";

type Point = { x: number; y: number };

const ELO_DRAG_HOLD_MS = 300;
const ELO_DRAG_THRESHOLD_PX = 4;
const FIXED_POWER_SYMBOL_SIZE = C1_ELO_PLAN_MODULE_SIZE;
const FIXED_POWER_SYMBOL_SCALE = FIXED_POWER_SYMBOL_SIZE / 32;
const FIXED_POWER_SYMBOL_HEIGHT = 27;
const SMOKE_SENSOR_TAG_ICON_URL = `data:image/svg+xml,${encodeURIComponent(smokeSensorSvg.replace("currentColor", "#ffffff"))}`;
const CAMERA_TAG_ICON_URL = `data:image/svg+xml,${encodeURIComponent(cameraSvg.replace("currentColor", "#ffffff"))}`;
const MOTION_SENSOR_TAG_ICON_URL = `data:image/svg+xml,${encodeURIComponent(motionSensorSvg.replace("currentColor", "#ffffff"))}`;

const SENSOR_ICON_URLS: Readonly<Record<string, string>> = {
  motion: motionSensorIconUrl,
  temperature: temperatureIconUrl,
  humidity: humiditySensorIconUrl,
  co2: co2SensorIconUrl,
  presence: presenceSensorIconUrl,
  brightness: brightnessSensorIconUrl,
  smoke: smokeSensorIconUrl,
};

function lightPlacementLabel(channelType: ChannelType | undefined): string | undefined {
  switch (channelType) {
    case ChannelType.Dimmer: return "%";
    case ChannelType.TuneableWhite: return itx("toolBar.twShortTag", "TW");
    case ChannelType.Rgb: return "RGB";
    default: return undefined;
  }
}

/** Ermittelt die darstellbaren Funktionen eines Sensors, ohne unbekannte Typen fälschlich zu bebildern. */
function sensorIconUrls(item: C1_EloItem, definition: C1_EloItem_Definition | undefined): string[] {
  // Manche atomaren Katalogeinträge (z. B. room.motionDetector) besitzen nur
  // defaultDetails und kein editierbares details-Schema. In diesem Fall werden
  // die Defaults nicht in die Instanz kopiert und müssen für die Darstellung
  // direkt aus der Definition gelesen werden.
  const sensorTypes = item.data.details?.sensorTypes ?? definition?.defaultDetails?.sensorTypes ?? [];
  const configuredIcons = sensorTypes
    .map((sensorType) => SENSOR_ICON_URLS[sensorType])
    .filter((url): url is string => Boolean(url));
  if (configuredIcons.length) return [...new Set(configuredIcons)];

  // Thermostate tragen in älteren Katalogständen nicht immer sensorTypes,
  // haben aber semantisch eindeutig eine Temperaturdarstellung.
  if (definition?.type === C1_EloItem_Type.Thermostat) return [temperatureIconUrl];

  // Präsenz und Helligkeit sind noch keine Enum-Werte. Definitionen können die
  // Symbole trotzdem über ihre stabile fachliche ID erhalten.
  const definitionId = (item.definition?.definitionId ?? "").toLowerCase();
  if (definitionId.includes("smoke") || definitionId.includes("rauch")) return [smokeSensorIconUrl];
  if (definitionId.includes("motion") || definitionId.includes("bewegung")) return [motionSensorIconUrl];
  if (definitionId.includes("presence") || definitionId.includes("praesenz")) return [presenceSensorIconUrl];
  if (definitionId.includes("brightness") || definitionId.includes("helligkeit")) return [brightnessSensorIconUrl];
  return [];
}

/** Selects a compact device symbol that remains self-contained in PDF exports. */
function deviceTagIconUrl(item: C1_EloItem, definition: C1_EloItem_Definition | undefined): string | undefined {
  if (definition?.type === C1_EloItem_Type.IntermediateDistribution) return getChannelTypePicture(ChannelType.IntermediateDistribution);
  if (definition?.type === C1_EloItem_Type.Camera) return CAMERA_TAG_ICON_URL;
  if (definition?.type !== C1_EloItem_Type.Sensor) return undefined;
  const icons = sensorIconUrls(item, definition);
  if (icons.includes(smokeSensorIconUrl)) return SMOKE_SENSOR_TAG_ICON_URL;
  if (icons.includes(motionSensorIconUrl)) return MOTION_SENSOR_TAG_ICON_URL;
  return undefined;
}

/** Ghosts cannot rely on the normal tag color to identify their function. */
function ghostTagIconUrls(item: C1_EloItem, definition: C1_EloItem_Definition | undefined): string[] {
  const type = definition?.type;
  if (type === C1_EloItem_Type.Sensor || type === C1_EloItem_Type.Thermostat) {
    return sensorIconUrls(item, definition);
  }
  const channel = type === C1_EloItem_Type.Plug ? ChannelType.Plug
    : type === C1_EloItem_Type.Heating ? ChannelType.Heating
    : type === C1_EloItem_Type.Blind ? ChannelType.Blind
    : type === C1_EloItem_Type.Jalousie ? ChannelType.Jalousie
    : type === C1_EloItem_Type.FixedPower
      ? (item.data.details?.voltage ?? definition?.defaultDetails?.voltage) === "400V" ? ChannelType.suply400V : ChannelType.suply230V
    : undefined;
  const icon = channel ? getChannelTypePicture(channel) : undefined;
  return icon ? [icon] : [];
}

/** Reuses the device pictures for the design view of window and door equipment. */
function openingDeviceIconUrl(definition: C1_EloItem_Definition | undefined): string | undefined {
  const channel = definition?.type === C1_EloItem_Type.Blind ? ChannelType.Blind
    : definition?.type === C1_EloItem_Type.Jalousie ? ChannelType.Jalousie
    : definition?.type === C1_EloItem_Type.WindowContact ? ChannelType.Lock : undefined;
  return channel ? getChannelTypePicture(channel) || undefined : undefined;
}

function sensorIconTiles(iconUrls: readonly string[], x: number, y: number, width: number, height: number) {
  const columns = iconUrls.length === 1 ? 1 : 2;
  const rows = Math.ceil(iconUrls.length / columns);
  const tileWidth = width / columns;
  const tileHeight = height / rows;
  return iconUrls.map((url, index) => ({
    url,
    x: x + (index % columns) * tileWidth,
    y: y + Math.floor(index / columns) * tileHeight,
    width: tileWidth,
    height: tileHeight,
  }));
}

function eloItemDefinition(item: C1_EloItem): C1_EloItem_Definition | undefined {
  return item.definition?.toDefinition();
}

function escapeTooltipHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return entities[character];
  });
}

function eloItemTooltipHtml(item: C1_EloItem): string {
  const definition = eloItemDefinition(item);
  const title = [item.owner?.tag, item.owner?.name].filter(Boolean).join(" ");
  const functionName = definition?.description[c1_state.language]
    || (definition ? C1_ELO_ITEM_TYPE_LABELS[definition.type] : "");
  const icon = (item.owner as BaseModel | undefined)?.treeViewModel?.icon;
  const iconHtml = icon ? renderToStaticMarkup(icon) : "";
  const heading = `<span style="display:inline-flex;align-items:center;gap:6px">${iconHtml}<strong>${escapeTooltipHtml(title)}</strong></span>`;
  return `${heading}<br />${escapeTooltipHtml(functionName)}`;
}

/** Assigns device colors, distinguishing blinds from contacts and highlighting unwired outlets. */
function tagBackgroundColor(eloItem: C1_EloItem): string {
  const details = eloItem.data.details;
  const colors = state.settings.drawingItemColors;
  const type = eloItem.definition?.type;
  if (type === C1_EloItem_Type.DoorStation) return colors.button;
  if (type === C1_EloItem_Type.Camera) return colors.sensor;
  if (type === C1_EloItem_Type.WindowContact) return colors.windowContact;
  if (type === C1_EloItem_Type.Sensor || type === C1_EloItem_Type.Thermostat) return colors.sensor;
  if (type === C1_EloItem_Type.Jalousie || type === C1_EloItem_Type.Blind) {
    return colors.jalousie;
  }
  if (eloItem.definition?.type === C1_EloItem_Type.Plug || details?.plugType) {
    const withoutCable = eloItem instanceof C1_Consumer && eloItem.consumer.noCable;
    return withoutCable ? colors.plugWithoutCable : colors.plug;
  }
  if (eloItem instanceof C1_Consumer) return eloItem.consumer.drawingItemColor();
  if (eloItem instanceof C1_Button) return colors.button;
  if (details?.buttonType) return colors.button;
  if (eloItem.definition?.type === C1_EloItem_Type.Light) return colors.light;
  if (details?.voltage) return colors.supply;
  if (details?.sensorTypes?.length) return colors.sensor;
  return colors.consumer;
}

function belongsToPlugGroup(item: C1_EloItem): boolean {
  let parent = item.parent;
  while (parent instanceof C1_EloItem) {
    if (eloItemDefinition(parent)?.type === C1_EloItem_Type.PlugGroup) return true;
    parent = parent.parent;
  }
  return false;
}

function plugGroupForItem(item: C1_EloItem): C1_EloGroup | undefined {
  let candidate: C1_EloItem | undefined = item;
  while (candidate) {
    if (candidate instanceof C1_EloGroup && eloItemDefinition(candidate)?.type === C1_EloItem_Type.PlugGroup) {
      return candidate;
    }
    candidate = candidate.parent instanceof C1_EloItem ? candidate.parent : undefined;
  }
  return undefined;
}

/** Technical outlet markers inherit the independently placed frame's mounting. */
function itemIndependentPlacement(item: C1_EloItem, plugGroup?: C1_EloGroup) {
  return item.interactivCreated ? item.data.interactivePlacement
    : plugGroup?.interactivCreated ? plugGroup.data.interactivePlacement : undefined;
}

function wallOutletItems(item: C1_EloItem): C1_EloItem[] {
  if (!(item instanceof C1_EloGroup)) return [];
  const repeatedFieldIndex = (child: C1_EloItem, fallback: number) => {
    return child.data.repeatIndex ?? fallback;
  };
  return item.items
    .map((child, index) => ({ child, index }))
    .filter(({ child }) => eloItemDefinition(child)?.type === C1_EloItem_Type.Plug)
    .sort((left, right) => repeatedFieldIndex(left.child, left.index) - repeatedFieldIndex(right.child, right.index))
    .map(({ child }) => child);
}

function isAncestorOfSelection(item: C1_EloItem, selection: C1_BaseObject<any> | undefined): boolean {
  let parent = selection instanceof C1_EloItem ? selection.parent : undefined;
  while (parent instanceof C1_EloItem) {
    if (parent === item) return true;
    parent = parent.parent;
  }
  return false;
}

function isDescendantOfSelection(item: C1_EloItem, selection: C1_BaseObject<any> | undefined): boolean {
  if (!(selection instanceof C1_EloItem)) return false;
  let parent = item.parent;
  while (parent instanceof C1_EloItem) {
    if (parent === selection) return true;
    parent = parent.parent;
  }
  return false;
}

function wallOutletSlots(item: C1_EloItem): WallOutletSlotType[] {
  const slotType = (plugType: C1_EloItem_PlugType | undefined): WallOutletSlotType => {
    if (plugType === C1_EloItem_PlugType.LAN) return "lan";
    if (plugType === C1_EloItem_PlugType.LAN2) return "lan2";
    if (plugType === C1_EloItem_PlugType.TV) return "tv";
    if (plugType === C1_EloItem_PlugType.USB) return "usb";
    if (plugType?.startsWith(C1_EloItem_PlugType.V230Switch)) return "schukoSwitched";
    return "schuko";
  };
  const slots = item instanceof C1_EloGroup
    ? wallOutletItems(item).map((child) => slotType(child.data.details?.plugType))
    : [];
  // Bestehende Projekte enthalten die Türsteckdose noch als atomares Item.
  // Bis zum automatischen Reconcile bleibt sie so ohne sichtbaren Bruch erhalten.
  return slots.length ? slots : item.data.details?.plugType ? [slotType(item.data.details.plugType)] : [];
}

function wallOutletSlotIndex(group: C1_EloGroup, item: C1_EloItem): number {
  const repeatedFieldIndex = (child: C1_EloItem, fallback: number) => {
    return child.data.repeatIndex ?? fallback;
  };
  return group.items
    .map((child, index) => ({ child, index }))
    .filter(({ child }) => eloItemDefinition(child)?.type === C1_EloItem_Type.Plug)
    .sort((left, right) => repeatedFieldIndex(left.child, left.index) - repeatedFieldIndex(right.child, right.index))
    .findIndex(({ child }) => child.id === item.id);
}

function switchedCircuitLabel(plugType: C1_EloItem_PlugType | undefined): string | undefined {
  if (plugType === C1_EloItem_PlugType.V230Switch1) return "1";
  if (plugType === C1_EloItem_PlugType.V230Switch2) return "2";
  if (plugType === C1_EloItem_PlugType.V230Switch3) return "3";
  return undefined;
}

function inheritedPlanView(item: C1_EloItem): C1_EloItemPlanView | undefined {
  let candidate: C1_EloItem | undefined = item;
  while (candidate) {
    const planView = eloItemDefinition(candidate)?.planView;
    if (planView) return planView;
    candidate = candidate.parent instanceof C1_EloItem ? candidate.parent : undefined;
  }
  return undefined;
}

function planViewPlacement(
  item: C1_EloItem,
  origin: Point,
  defaultWidth: number,
  defaultHeight: number,
  ownerRotation: number,
  ownerReflected: boolean,
  forcedAnchor?: C1_EloItemViewAnchor,
  wallLightMount?: { rotation: number; radiusRatio?: number },
) {
  const options = inheritedPlanView(item);
  const anchor = PLAN_VIEW_ANCHORS[options?.anchor ?? forcedAnchor ?? "center"];
  const width = options?.width ?? defaultWidth;
  const height = options?.height ?? defaultHeight;
  const rotation = options?.rotateWithOwner === false ? 0 : ownerRotation;
  const reflected = options?.reflectWithOwner === false ? false : ownerReflected;
  // Dock the visible circle's top to the wall, while its icon remains upright.
  const radius = Math.min(width, height) * (wallLightMount?.radiusRatio ?? 0.5);
  const mountAngle = ((wallLightMount?.rotation ?? rotation) - rotation) * Math.PI / 180;
  return {
    width,
    height,
    x: wallLightMount ? -width / 2 - Math.sin(mountAngle) * radius * (reflected ? -1 : 1) : -anchor.x * width,
    y: wallLightMount ? -height / 2 + Math.cos(mountAngle) * radius : -anchor.y * height,
    transform: `translate(${origin.x} ${origin.y}) rotate(${rotation}) scale(${reflected ? -1 : 1} 1)`,
    rotation,
    reflected,
  };
}

/** Position upright badges at the displayed image bounds, including rotated/reflected symbols. */
function planGraphicBadgePosition(origin: Point, placement: ReturnType<typeof planViewPlacement>, bounds = placement, side: "left" | "right" = "right"): Point {
  const angle = placement.rotation * Math.PI / 180;
  const corners = [
    [bounds.x, bounds.y], [bounds.x + bounds.width, bounds.y],
    [bounds.x, bounds.y + bounds.height], [bounds.x + bounds.width, bounds.y + bounds.height],
  ].map(([localX, localY]) => {
    const reflectedX = placement.reflected ? -localX : localX;
    return { x: origin.x + reflectedX * Math.cos(angle) - localY * Math.sin(angle),
      y: origin.y + reflectedX * Math.sin(angle) + localY * Math.cos(angle) };
  });
  return { x: (side === "left" ? Math.min : Math.max)(...corners.map(point => point.x)), y: Math.min(...corners.map(point => point.y)) };
}

export type C1_EloItemViewProps = {
  eloItem: C1_EloGroup;
  origin: Point;
  ownerSelected: boolean;
  rotation?: number;
  exteriorLightRotation?: number;
  reflect?: boolean;
  wallDockSide?: WallSide;
  /** Wall tangent for openings whose devices can lie on either side. */
  collisionWallRotation?: number;
  uprightItemTypes?: readonly C1_EloItem_Type[];
  placementProps?: Record<string, number>;
  ghostDefinitions?: C1_EloItem_Definition[];
  showGhosts?: boolean;
  displayPositionsPrepared?: boolean;
  preparedGhostItems?: ReturnType<typeof createEloGhostItems>;
  onLightImageBoundsLoaded?: () => void;
  onClick?: (eloItem: C1_EloItem) => void;
  onDoubleClick?: (eloItem: C1_EloItem) => void;
  onStartOwnerFurnitureDrag?: (event: React.PointerEvent<SVGElement>) => boolean;
};

/** Bounds of the normal symbol, excluding hover previews and editing controls, in model metres. */
function displayFootprint(item: C1_EloItem, position: Point, props: C1_EloItemViewProps, mode: C1_DisplayMode): DisplayFootprint | undefined {
  const definition = eloItemDefinition(item);
  const type = definition?.type;
  const technical = mode !== "design";
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  const electrical = ["EloConcept", "EloDetail", "interactionConcept"].includes(c1_state.editMode.value);
  if (!electrical && !item.interactivCreated) return;
  if (type === C1_EloItem_Type.Heating && !technical && (renderingPdf || !electrical)) return;
  const imagesAllowed = c1_state.floorImageRenderOptions.value?.excludeEloImages !== true;
  const isButton = type === C1_EloItem_Type.Button;
  const isLight = type === C1_EloItem_Type.Light;
  const isPlug = type === C1_EloItem_Type.Plug;
  const isFixedPower = type === C1_EloItem_Type.FixedPower;
  const compactGroup = type === C1_EloItem_Type.PlugGroup && !technical;
  const isGroup = type === C1_EloItem_Type.Group || type === C1_EloItem_Type.PlugGroup || (item instanceof C1_EloGroup && item.items.length > 0);
  const groupIcon = isGroup && !technical ? getC1EloDefinitionIconUrl(item.definition) : undefined;
  if (isGroup && !compactGroup && !groupIcon) return;
  if (!technical && item.parent instanceof C1_EloGroup && getC1EloDefinitionIconUrl(item.parent.definition)) return;
  if (!technical && isPlug && belongsToPlugGroup(item)) return;
  const independent = itemIndependentPlacement(item, technical && isPlug ? plugGroupForItem(item) : undefined);
  const rotation = independent?.rotation ?? props.rotation ?? 0;
  const reflected = props.reflect ?? false;
  const productId = item.data.details?.lightProductId ?? definition?.defaultDetails?.lightProductId;
  const wallLight = isLight && (productId === "symbol-light-wall" || definition?.usageCategory === "indoorWall" || definition?.usageCategory === "outdoorWall");
  const mountingRotation = independent?.rotation ?? (definition?.usageCategory === "outdoorWall" ? props.exteriorLightRotation ?? rotation : rotation);
  const wallSide = independent?.wallDocked !== undefined ? independent.side : props.wallDockSide;
  const wallMounted = wallLight || wallSide !== undefined || independent?.wallDocked !== undefined || definition?.usageCategory === "indoorWall" || definition?.usageCategory === "outdoorWall";
  const compactDeviceIcon = groupIcon ?? (technical ? deviceTagIconUrl(item, definition) : undefined);
  const compactFixedPower = isFixedPower && (item.data.details?.voltage ?? definition?.defaultDetails?.voltage ?? "230V") === "230V";
  const compact = isLight || isPlug || type === C1_EloItem_Type.Blind || type === C1_EloItem_Type.Jalousie || type === C1_EloItem_Type.WindowContact || type === C1_EloItem_Type.Heating || compactFixedPower || type === C1_EloItem_Type.DoorStation || !!compactDeviceIcon;
  const withSymbol = technical && !compact && item instanceof C1_Consumer && !!item.consumer.getImgSrc();
  const equipmentGraphic = imagesAllowed && (!!groupIcon || type === C1_EloItem_Type.Heating || type === C1_EloItem_Type.IntermediateDistribution || !!openingDeviceIconUrl(definition) || sensorIconUrls(item, definition).length > 0);
  const designGraphic = (!technical && (compactGroup || isPlug || isButton || isFixedPower || type === C1_EloItem_Type.Camera || equipmentGraphic || (imagesAllowed && isLight && !!getLightProductImageUrl(productId))))
    || (mode === "din" && renderingPdf && isButton);
  const scale = normalizeEloItemScale((c1_state.floorImageRenderOptions.value?.eloItemScale ?? c1_state.settings.eloItemScale)) / c1_state.svgPx;
  if (!designGraphic) {
    const offset = tagPlacementOffset({
      anchor: technical || type === C1_EloItem_Type.DoorStation ? wallLight ? "top-center" : inheritedPlanView(item)?.anchor : "center",
      wallSide, wallMounted: technical && wallMounted, rotation: mountingRotation, reflected, withSymbol,
    });
    return { x: position.x + offset.x * scale, y: position.y + offset.y * scale,
      halfWidth: (C1_TAG_RADIUS + 0.75) * scale, halfHeight: (withSymbol ? 14.75 : C1_TAG_RADIUS + 0.75) * scale,
      circle: mode !== "din" && !withSymbol && (!isButton || renderingPdf) };
  }
  const outlet = !technical && (compactGroup || isPlug);
  const upright = !!((!technical && wallLight) || independent || type === C1_EloItem_Type.Sensor || type === C1_EloItem_Type.WindowContact || (type && props.uprightItemTypes?.includes(type)));
  const wholeUpright = upright && !isButton && !outlet;
  const width = outlet ? Math.max(1, wallOutletSlots(item).length) * C1_ELO_PLAN_MODULE_SIZE : isLight ? 64 : C1_ELO_PLAN_MODULE_SIZE;
  const height = outlet ? C1_ELO_PLAN_MODULE_SIZE : isFixedPower ? FIXED_POWER_SYMBOL_HEIGHT : isLight ? 64 : C1_ELO_PLAN_MODULE_SIZE;
  const placement = planViewPlacement(item, {x:0,y:0}, width, height, wholeUpright ? 0 : rotation, wholeUpright ? false : reflected,
    isLight ? "center" : isFixedPower || compactGroup ? "top-center" : undefined,
    !technical && wallLight ? {rotation:mountingRotation,radiusRatio:getLightSymbolOutlineRadiusRatio(productId)} : undefined);
  const angle = placement.rotation * Math.PI / 180;
  let visible = placement;
  if (isLight) {
    const outlineRatio = getLightSymbolOutlineRadiusRatio(productId);
    if (outlineRatio !== undefined) {
      const centerX = (placement.x + placement.width / 2) * (placement.reflected ? -1 : 1);
      const centerY = placement.y + placement.height / 2;
      // The drawn outline circle encloses the standard symbol, not its padded SVG viewBox.
      const radius = (Math.min(placement.width, placement.height) * outlineRatio + 0.3) * scale;
      return {x:position.x+(centerX*Math.cos(angle)-centerY*Math.sin(angle))*scale,
        y:position.y+(centerX*Math.sin(angle)+centerY*Math.cos(angle))*scale,
        halfWidth:radius,halfHeight:radius,circle:true};
    }
    const imageUrl = getLightProductImageUrl(productId);
    const bounds = imageUrl ? cachedLightImageHitRect(imageUrl) : undefined;
    if (bounds) {
      const fit = Math.min(placement.width / bounds.imageWidth, placement.height / bounds.imageHeight);
      visible = {...placement,
        x:placement.x+(placement.width-bounds.imageWidth*fit)/2+bounds.x*fit,
        y:placement.y+(placement.height-bounds.imageHeight*fit)/2+bounds.y*fit,
        width:bounds.width*fit,height:bounds.height*fit};
    }
  }
  const corners = [0, 1].flatMap(ix => [0, 1].map(iy => {
    const x = (visible.x + ix * visible.width) * (placement.reflected ? -1 : 1), y = visible.y + iy * visible.height;
    return {x:(x * Math.cos(angle) - y * Math.sin(angle)) * scale, y:(x * Math.sin(angle) + y * Math.cos(angle)) * scale};
  }));
  const x1 = Math.min(...corners.map(p=>p.x)), x2 = Math.max(...corners.map(p=>p.x));
  const y1 = Math.min(...corners.map(p=>p.y)), y2 = Math.max(...corners.map(p=>p.y));
  return {x:position.x+(x1+x2)/2,y:position.y+(y1+y2)/2,halfWidth:(x2-x1)/2,halfHeight:(y2-y1)/2};
}

/** One floor-wide pass, shared by live SVG rendering and the SVG used for PDF export. */
export function prepareEloDisplayPositions(views: readonly C1_EloItemViewProps[], mode: C1_DisplayMode) {
  const records: DisplayCollisionItem[] = [];
  const facets = new Map<string, C1_EloItem>();
  const ghosts = new Map<C1_EloItemViewProps, ReturnType<typeof createEloGhostItems>>();
  for (const props of views) {
    for (const item of props.eloItem.allItems) item.temporaryDisplayPosition = undefined;
    props.eloItem.evaluateAbsolutePlacements(props.origin, props.rotation, props.reflect, props.placementProps);
    ghosts.set(props, props.showGhosts !== false && props.ownerSelected && c1_state.floorImageRenderOptions.value === null
      && ["EloConcept", "EloDetail", "interactionConcept"].includes(c1_state.editMode.value)
      ? createEloGhostItems(props.eloItem, props.origin, props.rotation ?? 0, props.reflect ?? false, props.placementProps ?? {}, props.ghostDefinitions, mode !== "design") : []);
  }
  for (const props of views) for (const item of props.eloItem.allItems) {
    if (facets.has(item.id) || !item.evaluatedAbsolutePosition) continue;
    const footprints = lightGroupPlanInstances(item, mode !== "design", props.rotation ?? 0, props.reflect ?? false, props.placementProps)
      .flatMap(instance => instance.position ? displayFootprint(item, instance.position, props, mode) ?? [] : []);
    if (!footprints.length) continue;
    let wallRotation = props.collisionWallRotation ?? (props.wallDockSide !== undefined ? props.rotation ?? 0 : undefined);
    for (let ancestor: C1_EloItem | undefined = item; ancestor; ancestor = ancestor.parent instanceof C1_EloItem ? ancestor.parent : undefined) {
      const placement = ancestor.data.interactivePlacement;
      if (placement?.wallDocked !== undefined) { wallRotation = placement.rotation; break; }
    }
    const direction = wallRotation === undefined ? undefined
      : eloWallCollisionDirection(item.evaluatedAbsolutePosition, props.origin, wallRotation);
    records.push({id:item.id,position:item.evaluatedAbsolutePosition,origin:props.origin,direction,wallDocked:wallRotation !== undefined,
      formulaPosition:item.spatialEvaluation.formulaAbsolutePosition ?? item.evaluatedAbsolutePosition,z:item.evaluatedZ,movable:true,footprints});
    facets.set(item.id,item);
  }
  for (const [id, position] of resolveEloDisplayCollisions(records)) facets.get(id)!.temporaryDisplayPosition = position;
  return ghosts;
}

/** Children of passage socket groups inherit the side used for room-plan exports. */
function openingEloSide(item: C1_EloItem): "inside" | "outside" | undefined {
  type SideNode = { definitionId?: string; parent?: unknown };
  const seen = new Set<object>();
  for (let node: SideNode | undefined = item; node && !seen.has(node); node = node.parent as SideNode | undefined) {
    seen.add(node);
    if (node.definitionId === "door-internal.button-outside") return "outside";
    if (node.definitionId?.startsWith("passage.")) return node.definitionId.includes("-outside-") ? "outside" : "inside";
  }
  return undefined;
}

export function C1_EloItemView({
  eloItem,
  origin,
  ownerSelected,
  rotation = 0,
  exteriorLightRotation,
  reflect = false,
  wallDockSide,
  uprightItemTypes = [],
  placementProps = {},
  ghostDefinitions,
  showGhosts = true,
  onClick,
  onDoubleClick,
  onStartOwnerFurnitureDrag,
  displayPositionsPrepared = false,
  preparedGhostItems,
  onLightImageBoundsLoaded,
}: C1_EloItemViewProps) {
  useOnChangeWatcher(uiModeWatcher);
  useOnChangeWatcher(state.dockManager);
  useOnChangeWatcher(state.selectionManager.displaySelections);
  useOnChangeWatcher(state.selectionManager.editorPins);
  useOnChangeWatcher(c1_state.lightEditDialogItemId);
  useOnChangeWatcher(eloItem.owner);
  useOnChangeWatcher(eloItem instanceof C1_Button ? eloItem.owner?.template : undefined);
  useOnChangeWatcher(c1_state.project.displayMode);
  useOnChangeWatcher(c1_state.settings);
  useOnChangeWatcher(c1_state.zoom);
  const { hideTooltip } = useTooltipController();
  const delayedClick = useDelayedClick(`${eloItem.id}:${c1_state.editMode.value}:${c1_state.currentFloorId.value}`);
  const developerMode = isUiModeDeveloper();
  const displayMode = resolvePlanDisplayMode(state.settings.applicationMode, c1_state.editMode.value, c1_state.project.displayMode.value, c1_state.floorImageRenderOptions.value?.displayMode);
  const dinMode = displayMode === "din";
  const technicalMode = displayMode !== "design";
  const showItems = (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") || c1_state.editMode.value === "interactionConcept";
  const showGroups = c1_state.showDebugEloGroupPositions.value;
  if (!displayPositionsPrepared) {
    for (const item of eloItem.allItems) item.temporaryDisplayPosition = undefined;
    eloItem.evaluateAbsolutePlacements(origin, rotation, reflect, placementProps);
  }
  const evaluatedItems = eloItem.allItems;
  const eloGhostItems = preparedGhostItems ?? (
    showGhosts && (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") && ownerSelected && c1_state.floorImageRenderOptions.value === null
      ? createEloGhostItems(eloItem, origin, rotation, reflect, placementProps, ghostDefinitions, technicalMode)
      : []);
  const eloGhostById = new Map(eloGhostItems.map((entry) => [entry.item.id, entry]));
  const renderedItems = [...evaluatedItems, ...eloGhostItems.map((entry) => entry.item)];
  const excludeEloImages = c1_state.floorImageRenderOptions.value?.excludeEloImages === true;
  const lightImageUrls = [
    ...new Set(
      (excludeEloImages ? [] : renderedItems)
        .map((item) => getLightProductImageUrl(item.data.details?.lightProductId ?? item.definition?.defaultDetails?.lightProductId))
        .filter((url): url is string => Boolean(url)),
    ),
  ];
  const lightImageUrlsKey = lightImageUrls.join("|");
  const processedGraphicUrls = useProcessedEloGraphicUrls(
    [...lightImageUrls, ...Object.values(SENSOR_ICON_URLS),
      ...renderedItems.flatMap(item => {
        const definition = eloItemDefinition(item);
        const url = definition?.type === C1_EloItem_Type.IntermediateDistribution
          ? getChannelTypePicture(ChannelType.IntermediateDistribution)
          : openingDeviceIconUrl(definition);
        return url ? [url] : [];
      })],
    c1_state.settings.eloGraphicContrast,
  );
  const vectorContrastFactor = contrastFactor(c1_state.settings.eloGraphicContrast);
  const vectorContrastFilterId = `c1-elo-white-contrast-${eloItem.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const selectionImageFilterId = `c1-elo-selection-image-${eloItem.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const parentSelectionImageFilterId = `c1-elo-parent-selection-image-${eloItem.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const ghostImageFilterId = `c1-elo-ghost-image-${eloItem.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const vectorContrastProps = {
    "data-c1-elo-vector-contrast": String(c1_state.settings.eloGraphicContrast),
    style: { filter: `url(#${vectorContrastFilterId})` },
  };
  const [, setLightHitRectVersion] = useState(0);
  const [hoveredItemId, setHoveredItemId] = useState<string>();
  const [hoveredOutletChildId, setHoveredOutletChildId] = useState<string>();
  const [draggingItemId, setDraggingItemId] = useState<string>();
  const suppressedClickItemId = useRef<string>();
  const mobileTap = useRef<{ item: C1_EloItem; area: Element; pointerId: number; x: number; y: number; moved: boolean }>();
  const startMobileTap = (event: React.PointerEvent<SVGElement>, item: C1_EloItem) => {
    const area = event.currentTarget.closest(".C1_drawingArea");
    if (event.button !== 0 || area?.getAttribute("data-c1-mobile-edit") !== "true") return;
    mobileTap.current = { item, area, pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  };
  const moveMobileTap = (event: React.PointerEvent<SVGElement>) => {
    const tap = mobileTap.current;
    if (tap?.pointerId === event.pointerId && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) >= ELO_DRAG_THRESHOLD_PX) tap.moved = true;
  };
  const ownerFurniturePointer = useRef<{
    itemId: string;
    pointerId: number;
    target: SVGElement;
    startClient: Point;
    moved: boolean;
  }>();
  const pendingDrag = useRef<{
    item: C1_EloItem;
    pointerId: number;
    target: SVGElement;
    startClient: Point;
    latestClient: Point;
    latestPointer: Point;
    grabOffset: Point;
    startedAt: number;
    dragging: boolean;
    timer: number;
    disableDocking?: boolean;
  }>();
  const ghostDrag = useRef<{
    item: C1_EloItem;
    pointerId: number;
    canvas: Element;
    startClient: Point;
    grabOffset: Point;
    dragging: boolean;
  }>();

  const clearPendingDrag = () => {
    const pending = pendingDrag.current;
    if (!pending) return;
    window.clearTimeout(pending.timer);
    if (pending.target.hasPointerCapture(pending.pointerId)) pending.target.releasePointerCapture(pending.pointerId);
    pendingDrag.current = undefined;
  };

  useEffect(() => () => clearPendingDrag(), []);
  useEffect(() => {
    let active = true;
    void Promise.all(lightImageUrls.map(loadLightImageHitRect)).then(() => {
      if (active) setLightHitRectVersion((version) => version + 1);
      if (active && lightImageUrls.length) onLightImageBoundsLoaded?.();
    });
    return () => {
      active = false;
    };
  }, [lightImageUrlsKey, onLightImageBoundsLoaded]);

  const setDraggedPosition = (item: C1_EloItem, pointer: Point, grabOffset: Point, disableDocking = false) => {
    // Der beim Pointerdown gegriffene Punkt bleibt relativ zum Item-Ursprung
    // unverändert. So springt das Item beim Start des Drags nicht zur Mausmitte.
    let absoluteX = pointer.x - grabOffset.x;
    let absoluteY = pointer.y - grabOffset.y;
    if (item.interactivCreated && item.data.interactivePlacement) {
      item.moveInteractiveTo({ x: absoluteX, y: absoluteY }, disableDocking);
      return;
    }
    const owner = eloRootAndOwner(item)?.owner;
    const room = owner instanceof C1_Furniture ? owner.room : undefined;
    if (room) {
      // Erst wie bei Einzelplatzierungen im Raum andocken, danach in Möbelkoordinaten zurückrechnen.
      const rect = room.innerRect();
      const placement = moveInteractiveEloPlacement(
        {paletteId: "", relativePosition: {x: 0, y: 0}, rotation: 0},
        {x: absoluteX, y: absoluteY}, rect, [], disableDocking, room.placementGeometry(),
      );
      const snapped = interactiveEloAbsolutePosition(placement, rect);
      absoluteX = snapped.x;
      absoluteY = snapped.y;
    }
    const dx = absoluteX - origin.x;
    const dy = absoluteY - origin.y;
    const rad = (rotation * Math.PI) / 180;
    const rotatedX = dx * Math.cos(rad) + dy * Math.sin(rad);
    const localY = -dx * Math.sin(rad) + dy * Math.cos(rad);
    const localX = reflect ? -rotatedX : rotatedX;
    item.setPosition({
      ...item.position,
      x: Math.round(localX * 1000) / 1000,
      y: Math.round(localY * 1000) / 1000,
    });
  };

  const updateDraggedPosition = (pending: NonNullable<typeof pendingDrag.current>) => {
    setDraggedPosition(pending.item, pending.latestPointer, pending.grabOffset, pending.disableDocking);
  };

  useEffect(() => {
    const moveSelected = (event: Event) => {
      if (event.defaultPrevented) return;
      const request = (event as CustomEvent<MobileEloMoveRequest>).detail;
      const selected = eloItem.allItems.find(item => item.id === request.itemId && !item.owner?.isDeleted);
      if (!selected) return;
      event.preventDefault();
      if (request.phase === "move") setDraggedPosition(selected, request.point, {x: 0, y: 0}, request.disableDocking);
      if (request.phase === "end") detachEloItemWithToast(selected, request.point, request.disableDocking);
    };
    window.addEventListener(MOBILE_SELECTED_ELO_MOVE, moveSelected);
    return () => window.removeEventListener(MOBILE_SELECTED_ELO_MOVE, moveSelected);
  });

  useEffect(() => {
    const moveGhost = (event: PointerEvent) => {
      const pending = ghostDrag.current;
      if (!pending || pending.pointerId !== event.pointerId) return;
      const pointer = clientToPlan(pending.canvas, event.clientX, event.clientY, c1_state.view);
      if (!pointer) return;
      if (!pending.dragging) {
        const distance = Math.hypot(event.clientX - pending.startClient.x, event.clientY - pending.startClient.y);
        if (distance < ELO_DRAG_THRESHOLD_PX) return;
        pending.dragging = true;
        setDraggingItemId(pending.item.id);
      }
      event.preventDefault();
      event.stopPropagation();
      setDraggedPosition(pending.item, pointer, pending.grabOffset, event.shiftKey || event.ctrlKey);
    };
    const finishGhost = (event: PointerEvent) => {
      const pending = ghostDrag.current;
      if (!pending || pending.pointerId !== event.pointerId) return;
      ghostDrag.current = undefined;
      setDraggingItemId(undefined);
      if (pending.dragging) {
        const pointer = clientToPlan(pending.canvas, event.clientX, event.clientY, c1_state.view);
        if (pointer && event.type !== "pointercancel") detachEloItemWithToast(pending.item,
          {x: pointer.x - pending.grabOffset.x, y: pointer.y - pending.grabOffset.y}, event.shiftKey || event.ctrlKey);
        event.preventDefault();
        event.stopPropagation();
        c1_state.eventLogger.addEvent(C1_EventKey.EloItemMoved, {
          eloItemId: pending.item.id,
          definitionId: pending.item.definition?.definitionId ?? "",
        });
      }
      queueMicrotask(() => state.undoManager.userInteractionEnded("Elektroelement verschieben"));
    };
    const cancelMobileDrag = (event: Event) => {
      if (mobileTap.current?.area === event.target) mobileTap.current = undefined;
      const belongsToPlan = (target: Element | undefined) => target?.closest(".C1_drawingArea") === event.target;
      let moving = false;
      let cancelled = false;
      if (belongsToPlan(pendingDrag.current?.target)) {
        moving ||= !!pendingDrag.current?.dragging;
        clearPendingDrag(); cancelled = true;
      }
      if (belongsToPlan(ghostDrag.current?.canvas)) {
        moving ||= !!ghostDrag.current?.dragging;
        ghostDrag.current = undefined; cancelled = true;
      }
      if (belongsToPlan(ownerFurniturePointer.current?.target)) {
        moving ||= !!ownerFurniturePointer.current?.moved;
        ownerFurniturePointer.current = undefined; cancelled = true;
      }
      if (cancelled) setDraggingItemId(undefined);
      if (moving) queueMicrotask(() => state.undoManager.userInteractionEnded("Elektroelement verschieben"));
    };
    window.addEventListener(PLAN_CANCEL_OBJECT_DRAG, cancelMobileDrag);
    window.addEventListener("pointermove", moveGhost, true);
    window.addEventListener("pointerup", finishGhost, true);
    window.addEventListener("pointercancel", finishGhost, true);
    return () => {
      window.removeEventListener(PLAN_CANCEL_OBJECT_DRAG, cancelMobileDrag);
      window.removeEventListener("pointermove", moveGhost, true);
      window.removeEventListener("pointerup", finishGhost, true);
      window.removeEventListener("pointercancel", finishGhost, true);
    };
  });

  const activatePendingDrag = (forceAfterHold = false) => {
    const pending = pendingDrag.current;
    if (!pending || pending.dragging) return;
    const delta = Math.hypot(
      pending.latestClient.x - pending.startClient.x,
      pending.latestClient.y - pending.startClient.y,
    );
    const heldLongEnough = performance.now() - pending.startedAt >= ELO_DRAG_HOLD_MS;
    if (!forceAfterHold && !heldLongEnough && delta < ELO_DRAG_THRESHOLD_PX) return;
    pending.dragging = true;
    setDraggingItemId(pending.item.id);
    updateDraggedPosition(pending);
  };

  const startPotentialDrag = (event: React.PointerEvent<SVGElement>, item: C1_EloItem, dragItem: C1_EloItem = item) => {
    event.stopPropagation();
    const displayPosition = dragItem.displayPosition;
    if (event.button !== 0 || !displayPosition) return;
    if (onStartOwnerFurnitureDrag?.(event)) {
      event.currentTarget.setPointerCapture(event.pointerId);
      ownerFurniturePointer.current = {
        itemId: item.id,
        pointerId: event.pointerId,
        target: event.currentTarget,
        startClient: { x: event.clientX, y: event.clientY },
        moved: false,
      };
      return;
    }
    const pointer = clientToPlan(
      event.currentTarget.closest(".C1_canvas"),
      event.clientX,
      event.clientY,
      c1_state.view,
    );
    if (!pointer) return;
    clearPendingDrag();
    event.currentTarget.setPointerCapture(event.pointerId);
    const pending = {
      item: dragItem,
      pointerId: event.pointerId,
      target: event.currentTarget,
      startClient: { x: event.clientX, y: event.clientY },
      latestClient: { x: event.clientX, y: event.clientY },
      latestPointer: pointer,
      grabOffset: {
        x: pointer.x - displayPosition.x,
        y: pointer.y - displayPosition.y,
      },
      startedAt: performance.now(),
      dragging: false,
      timer: 0,
      disableDocking: event.shiftKey || event.ctrlKey,
    };
    pending.timer = window.setTimeout(() => activatePendingDrag(true), ELO_DRAG_HOLD_MS);
    pendingDrag.current = pending;
  };

  const movePotentialDrag = (event: React.PointerEvent<SVGGElement>) => {
    const ownerPointer = ownerFurniturePointer.current;
    if (ownerPointer?.pointerId === event.pointerId) {
      if (
        Math.hypot(event.clientX - ownerPointer.startClient.x, event.clientY - ownerPointer.startClient.y) >=
        ELO_DRAG_THRESHOLD_PX
      )
        ownerPointer.moved = true;
      return;
    }
    const pending = pendingDrag.current;
    if (!pending || pending.pointerId !== event.pointerId) return;
    const pointer = clientToPlan(
      event.currentTarget.closest(".C1_canvas"),
      event.clientX,
      event.clientY,
      c1_state.view,
    );
    if (!pointer) return;
    pending.latestClient = { x: event.clientX, y: event.clientY };
    pending.latestPointer = pointer;
    pending.disableDocking = event.shiftKey || event.ctrlKey;
    if (!pending.dragging) activatePendingDrag();
    if (!pending.dragging) return;
    event.preventDefault();
    event.stopPropagation();
    updateDraggedPosition(pending);
  };

  const finishPotentialDrag = (event: React.PointerEvent<SVGGElement>) => {
    const ownerPointer = ownerFurniturePointer.current;
    if (ownerPointer?.pointerId === event.pointerId) {
      if (ownerPointer.moved) suppressedClickItemId.current = ownerPointer.itemId;
      ownerFurniturePointer.current = undefined;
      return;
    }
    const pending = pendingDrag.current;
    if (!pending || pending.pointerId !== event.pointerId) return;
    const wasDragging = pending.dragging;
    const itemId = pending.item.id;
    if (wasDragging && event.type !== "pointercancel") detachEloItemWithToast(pending.item,
      {x: pending.latestPointer.x - pending.grabOffset.x, y: pending.latestPointer.y - pending.grabOffset.y}, pending.disableDocking);
    clearPendingDrag();
    if (!wasDragging) return;
    event.preventDefault();
    event.stopPropagation();
    suppressedClickItemId.current = itemId;
    c1_state.eventLogger.addEvent(C1_EventKey.EloItemMoved, {
      eloItemId: pending.item.id,
      definitionId: pending.item.definition?.definitionId ?? "",
    });
    window.setTimeout(() => {
      if (suppressedClickItemId.current === itemId) suppressedClickItemId.current = undefined;
    });
    queueMicrotask(() => state.undoManager.userInteractionEnded("Elektroelement verschieben"));
  };

  /** Mobile taps select on release even when pointer capture prevents a later native click. */
  const finishMobileEloPointer = (event: React.PointerEvent<SVGGElement>, clickItemId: string) => {
    moveMobileTap(event);
    const tap = mobileTap.current?.pointerId === event.pointerId ? mobileTap.current : undefined;
    const dragged = pendingDrag.current?.dragging || ownerFurniturePointer.current?.moved;
    finishPotentialDrag(event);
    if (!tap) return;
    mobileTap.current = undefined;
    if (event.type !== "pointerup" || tap.moved || dragged || tap.item.owner?.isDeleted) return;
    delayedClick.cancel();
    c1_state.setSelection(tap.item);
    state.dockManager.handleItemSelection(tap.item);
    c1_state.eventLogger.addEvent(C1_EventKey.EloItemDetailsSelected, {
      eloItemId: tap.item.id, definitionId: tap.item.definition?.definitionId ?? "",
    });
    suppressedClickItemId.current = clickItemId;
    window.setTimeout(() => {
      if (suppressedClickItemId.current === clickItemId) suppressedClickItemId.current = undefined;
    });
  };

  const applyGhostAction = (
    action: C1_EloGhostAction,
    preferredDefinitionId?: string,
    deferInteractionEnd = false,
  ): C1_EloItem | undefined => {
    const context = eloRootAndOwner(eloItem);
    if (!context) return;
    const previousIds = new Set(context.root.allItems.map((item) => item.id));
    const currentRoot = context.root.toLegacyTreeData();
    const nextRoot =
      action.type === "append-repeat"
        ? appendRepeatedItem(
            currentRoot,
            action.groupItemId,
            action.definition,
            c1_state.project.equipmentStandard,
          )
        : appendEloItem(currentRoot, action.parentItemId, action.definition, c1_state.project.equipmentStandard);
    if (nextRoot === currentRoot) return;
    const addedData: C1_LegacyEloItemTreeData[] = [];
    const collectAddedData = (item: C1_LegacyEloItemTreeData) => {
      if (!previousIds.has(item.id)) addedData.push(item);
      item.items.forEach(collectAddedData);
    };
    collectAddedData(nextRoot);
    const selectedData = addedData.find((item) => item.definitionId === preferredDefinitionId) ?? addedData[0];
    commitOwnerEloTree(
      context.owner,
      nextRoot,
      selectedData?.id ?? (action.type === "append-repeat" ? action.groupItemId : action.parentItemId),
      !deferInteractionEnd,
      ["EloConcept", "EloDetail", "interactionConcept"].includes(c1_state.editMode.value),
    );
    return selectedData ? materializedRootForEloOwner(context.owner)?.findItem(selectedData.id) : undefined;
  };
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  const selection = renderingPdf ? undefined : c1_state.selection;
  // SVG uses paint order instead of z-index. Hovered items are rendered late;
  // the container of a selected Steckdoseneinsatz is rendered last so its
  // inline flyout always remains above neighboring EloItems.
  const selectedContainerId =
    selection instanceof C1_EloItem &&
    selection.parent instanceof C1_EloItem &&
    eloItemDefinition(selection.parent)?.type === C1_EloItem_Type.PlugGroup
      ? selection.parent.id
      : selection instanceof C1_EloItem
        ? selection.id
        : undefined;
  // Moving a hovered preview in the DOM can trigger leave/enter again beneath a stationary pointer.
  const hoveredPaintId = hoveredItemId && !eloGhostById.has(hoveredItemId) ? hoveredItemId : undefined;
  const latePaintIds = [...new Set([hoveredPaintId, selectedContainerId].filter((id): id is string => Boolean(id)))];
  const items = [
    ...renderedItems.filter((item) => !latePaintIds.includes(item.id)),
    ...latePaintIds.flatMap((id) => renderedItems.filter((item) => item.id === id)),
  ];
  const electricallyInteractive = (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") || c1_state.editMode.value === "interactionConcept";
  const eloItemsMovable = (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail");
  const showHeating = technicalMode || (c1_state.floorImageRenderOptions.value === null && electricallyInteractive);

  if (!electricallyInteractive && !items.some(item => item.interactivCreated)) {
    return null;
  }

  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const normalizedTextRotation = ((((rotation - 90 + 180) % 360) + 360) % 360) - 180;
  const textWouldBeUpsideDown = Math.abs(Math.abs(normalizedTextRotation) - 180) < 0.001;
  const textRotation = textWouldBeUpsideDown ? 0 : normalizedTextRotation;
  const rotationRad = (rotation * Math.PI) / 180;
  const labelOffset = 9;
  const labelOffsetX = textWouldBeUpsideDown ? labelOffset : Math.sin(rotationRad) * labelOffset;
  const labelOffsetY = textWouldBeUpsideDown ? 0 : -Math.cos(rotationRad) * labelOffset;

  return (
    <g
      className="C1_svgEloDebug"
      data-c1-svg-layer="elo-debug"
      pointerEvents={electricallyInteractive ? "visiblePainted" : "none"}
    >
      <defs>
        <filter id={vectorContrastFilterId} colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncR type="linear" slope={vectorContrastFactor} intercept={1 - vectorContrastFactor} />
            <feFuncG type="linear" slope={vectorContrastFactor} intercept={1 - vectorContrastFactor} />
            <feFuncB type="linear" slope={vectorContrastFactor} intercept={1 - vectorContrastFactor} />
            <feFuncA type="identity" />
          </feComponentTransfer>
        </filter>
        <C1_OrangeSelectionFilter id={selectionImageFilterId} />
        <filter id={parentSelectionImageFilterId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.2120 0.7130 0.0720 0 0
                    0.1913 0.6437 0.0650 0 0
                    0.1658 0.5579 0.0563 0 0
                    0      0      0      1 0"
          />
        </filter>
        <filter id={ghostImageFilterId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.50 0    0    0 0.02
                    0    0.50 0    0 0.32
                    0 0    0.50    0 0.03
                    0 0    0       1 0"
          />
        </filter>
      </defs>
      {items.flatMap(item => lightGroupPlanInstances(item, technicalMode, rotation, reflect, placementProps)).map(({ item, position, suffix }) => {
        if (!electricallyInteractive && !item.interactivCreated) return null;
        const ghostEntry = eloGhostById.get(item.id);
        const isGhost = Boolean(ghostEntry);
        const definition = eloItemDefinition(item);
        if (!technicalMode && !electricallyInteractive && (definition?.type === C1_EloItem_Type.Blind || definition?.type === C1_EloItem_Type.Jalousie)) return null;
        const isGroup =
          definition?.type === C1_EloItem_Type.Group ||
          definition?.type === C1_EloItem_Type.PlugGroup ||
          (item instanceof C1_EloGroup && item.items.length > 0);
        const compactPlugGroup = definition?.type === C1_EloItem_Type.PlugGroup && !technicalMode;
        const groupIcon = isGroup && !technicalMode ? getC1EloDefinitionIconUrl(item.definition) : undefined;
        if (!technicalMode && item.parent instanceof C1_EloGroup && getC1EloDefinitionIconUrl(item.parent.definition)) return null;
        if (!technicalMode && definition?.type === C1_EloItem_Type.Plug && belongsToPlugGroup(item)) return null;
        if (!showHeating && definition?.type === C1_EloItem_Type.Heating) return null;
        if (isGroup && !compactPlugGroup && !groupIcon && (!developerMode || !showGroups)) return null;
        if (!isGroup && developerMode && !showItems && !item.interactivCreated) return null;
        if (!position) return null;
        const isButton = definition?.type === C1_EloItem_Type.Button;
        // Pin indicators in the plan are temporarily hidden.
        // const showButtonPin = isButton && !isGhost && !!item.owner
        //   && state.selectionManager.editorPins.get("eloEditor") === item.owner
        //   && c1_state.floorImageRenderOptions.value === null;
        const selectedForDisplay = !renderingPdf && (
          state.selectionManager.editorPins.get("eloEditor")?.c1 === item ||
          state.selectionManager.editorPins.get("consumerEditor")?.c1 === item ||
          (isButton && state.selectionManager.displaySelections.get("buttonEditor")?.c1 === item));
        const isSelected = !isGhost && (selection === item || selectedForDisplay);
        const selectedChild =
          !isGhost && selection instanceof C1_EloItem && selection.parent === item ? selection : undefined;
        const tooltipEnabled = !isSelected && !selectedChild;
        // Die Richtung der indirekten Auswahl bleibt explizit erhalten: Ein
        // Vorfahr der Auswahl ist parentSelected, ein Nachfahr childSelected.
        // Beide verwenden derzeit dieselbe Darstellung, bleiben semantisch
        // jedoch getrennte Zustände.
        const parentSelected = !isGhost && isAncestorOfSelection(item, selection);
        const independentOwner = independentEloOwner(item);
        const childSelected = !renderingPdf && !isGhost && ((!independentOwner && ownerSelected) || isDescendantOfSelection(item, selection));
        const indirectlySelected = parentSelected || childSelected;
        const imageSelectionFilter =
          isSelected && !compactPlugGroup
            ? `url(#${selectionImageFilterId})`
            : indirectlySelected
              ? `url(#${parentSelectionImageFilterId})`
              : undefined;
        const x = (position.x - view.x1) * PX;
        const y = (position.y - view.y1) * PX;
        const tag = isGhost ? "" : item.owner?.tag.trim() ? `${item.owner.tag.trim()}${suffix}` : "";
        const displayedTag = tag || "+";
        const tagFontSize = Math.max(3.6, Math.min(5.5, 16 / (Math.max(displayedTag.length, 1) * 0.62)));
        const labelDirection = isGroup ? -1 : 1;
        const labelX = x + labelOffsetX * labelDirection;
        const labelY = y + labelOffsetY * labelDirection;
        const isLight = definition?.type === C1_EloItem_Type.Light;
        const compactLightTag = technicalMode && isLight;
        const isPlug = definition?.type === C1_EloItem_Type.Plug;
        const plugType = item.data.details?.plugType ?? definition?.defaultDetails?.plugType;
        const compactPlugTag = technicalMode && isPlug;
        const hasPlugFunction = compactPlugTag && !!plugType && plugType !== C1_EloItem_PlugType.V230;
        const isBlind = definition?.type === C1_EloItem_Type.Jalousie || definition?.type === C1_EloItem_Type.Blind;
        const isWindowContact = definition?.type === C1_EloItem_Type.WindowContact;
        const compactWindowContactTag = technicalMode && isWindowContact;
        const isFixedPower = definition?.type === C1_EloItem_Type.FixedPower;
        const isCamera = definition?.type === C1_EloItem_Type.Camera;
        const fixedPowerVoltage = item.data.details?.voltage ?? definition?.defaultDetails?.voltage;
        const compactFixedPowerTag = technicalMode && isFixedPower && (fixedPowerVoltage ?? "230V") === "230V";
        const compactDeviceIcon = groupIcon ?? (technicalMode ? deviceTagIconUrl(item, definition) : undefined);
        const ghostIcons = technicalMode && isGhost && !compactDeviceIcon && !hasPlugFunction && !compactFixedPowerTag
          ? ghostTagIconUrls(item, definition) : [];
        const ghostFunctionLabel = technicalMode && isGhost
          ? definition?.type === C1_EloItem_Type.DoorStation ? itx("c1.ui.Tur", "Tür")
            : definition?.type === C1_EloItem_Type.DoorLock ? "Schloss"
            : definition?.type === C1_EloItem_Type.Sensor && !compactDeviceIcon && !ghostIcons.length ? itx("permanentPower.sensor", "Sensor") : undefined
          : undefined;
        const hasTagFunction = compactLightTag || hasPlugFunction || compactWindowContactTag || compactFixedPowerTag || !!compactDeviceIcon || ghostIcons.length > 0 || !!ghostFunctionLabel;
        const isHeating = definition?.type === C1_EloItem_Type.Heating;
        const usesCompactTag = isLight || isPlug || isBlind || isWindowContact || isHeating || compactFixedPowerTag || definition?.type === C1_EloItem_Type.DoorStation || !!compactDeviceIcon;
        const consumerSymbol = technicalMode && !usesCompactTag && item instanceof C1_Consumer
          ? item.consumer.getImgSrc()
          : undefined;
        const channelType = item.data.details?.channelType ?? definition?.defaultDetails?.channelType;
        const placementLabel = lightPlacementLabel(channelType);
        const isDaliLight = (item.data.details?.isDali ?? definition?.defaultDetails?.isDali) === true;
        const showSwitchDebugPoint =
          developerMode && channelType === ChannelType.Switch;
        const openingDeviceIcon = openingDeviceIconUrl(definition);
        const equipmentIcons = excludeEloImages
          ? []
          : (groupIcon
              ? [groupIcon]
              : definition?.type === C1_EloItem_Type.Heating
              ? [temperatureIconUrl]
              : definition?.type === C1_EloItem_Type.IntermediateDistribution
              ? [getChannelTypePicture(ChannelType.IntermediateDistribution)]
              : openingDeviceIcon ? [openingDeviceIcon]
              : sensorIconUrls(item, definition)
            ).map((url) => processedGraphicUrls.get(url) ?? url);
        const showEquipmentDesignView = !technicalMode && equipmentIcons.length > 0;
        const lightProductId = item.data.details?.lightProductId ?? definition?.defaultDetails?.lightProductId;
        const lightImageSourceUrl =
          !excludeEloImages && isLight
            ? getLightProductImageUrl(lightProductId)
            : undefined;
        const lightImageUrl = lightImageSourceUrl
          ? (processedGraphicUrls.get(lightImageSourceUrl) ?? lightImageSourceUrl)
          : undefined;
        const lightImageHitRect = lightImageSourceUrl ? cachedLightImageHitRect(lightImageSourceUrl) : undefined;
        const lightOutlineRadiusRatio = getLightSymbolOutlineRadiusRatio(lightProductId);
        const wallLight = isLight && (lightProductId === "symbol-light-wall" ||
          definition?.usageCategory === "indoorWall" || definition?.usageCategory === "outdoorWall");
        const designOutlet = !technicalMode && (compactPlugGroup || isPlug);
        const tintGhostGraphic = isGhost && (designOutlet || showEquipmentDesignView ||
          (!technicalMode && (isFixedPower || isCamera || (isButton && item instanceof C1_Button) || (isLight && !!lightImageUrl))));
        const outletSlots = designOutlet ? wallOutletSlots(item) : [];
        const showCompactPlugGroupAdd = Boolean(
          compactPlugGroup &&
            (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
            selectedChild &&
            definition &&
            repeatItemCount(definition, item.toLegacyTreeData()) < repeatItemLimit(definition),
        );
        const outletWidth =
          Math.max(1, outletSlots.length) * C1_ELO_PLAN_MODULE_SIZE;
        const outletHeight = C1_ELO_PLAN_MODULE_SIZE;
        const hoverPlugGroup = technicalMode && isPlug ? plugGroupForItem(item) : undefined;
        const hoverOutletSlots = technicalMode && isPlug ? wallOutletSlots(hoverPlugGroup ?? item) : [];
        const hoveredOutletIndex = hoverPlugGroup ? wallOutletSlotIndex(hoverPlugGroup, item) : 0;
        const hoverOutletWidth = Math.max(44, hoverOutletSlots.length * 36);
        const independentPlacement = itemIndependentPlacement(item, hoverPlugGroup);
        const mountingRotation = independentPlacement?.rotation ??
          (definition?.usageCategory === "outdoorWall" ? exteriorLightRotation ?? rotation : rotation);
        const tagWallSide = independentPlacement?.wallDocked !== undefined ? independentPlacement.side : wallDockSide;
        const wallMounted = wallLight || tagWallSide !== undefined || independentPlacement?.wallDocked !== undefined ||
          definition?.usageCategory === "indoorWall" || definition?.usageCategory === "outdoorWall";
        const tagOffset = tagPlacementOffset({
          anchor: technicalMode || definition?.type === C1_EloItem_Type.DoorStation
            ? wallLight ? "top-center" : inheritedPlanView(item)?.anchor
            : "center",
          wallSide: tagWallSide,
          wallMounted: technicalMode && wallMounted,
          rotation: mountingRotation,
          reflected: reflect,
          withSymbol: !!consumerSymbol,
        });
        const renderUpright = Boolean((!technicalMode && wallLight) || independentPlacement || (definition && (
          definition.type === C1_EloItem_Type.Sensor ||
          definition.type === C1_EloItem_Type.WindowContact ||
          uprightItemTypes.includes(definition.type)
        )));
        // Outlet frames follow the wall just like the placement preview, including standalone groups.
        const renderWholeItemUpright = renderUpright && !isButton && !designOutlet;
        const baseSvgPlacement = planViewPlacement(
          item,
          { x, y },
          designOutlet
            ? outletWidth
            : isFixedPower && !technicalMode
              ? FIXED_POWER_SYMBOL_SIZE
              : isLight
                ? 64
                : C1_ELO_PLAN_MODULE_SIZE,
          designOutlet
            ? outletHeight
            : isFixedPower && !technicalMode
              ? FIXED_POWER_SYMBOL_HEIGHT
              : isLight
                ? 64
                : C1_ELO_PLAN_MODULE_SIZE,
          renderWholeItemUpright ? 0 : independentPlacement?.rotation ?? rotation,
          renderWholeItemUpright ? false : reflect,
          isLight ? "center" : isFixedPower || compactPlugGroup ? "top-center" : undefined,
          !technicalMode && wallLight ? {
            rotation: mountingRotation,
            radiusRatio: lightOutlineRadiusRatio,
          } : undefined,
        );
        // Selection adds a preview slot, but must not change the existing group's origin or scale.
        // The renderer and inline-editor hit areas both use these same extended bounds.
        const svgPlacement = showCompactPlugGroupAdd
          ? { ...baseSvgPlacement, ...wallOutletPreviewBounds(baseSvgPlacement, outletSlots.length) }
          : baseSvgPlacement;

        let addBadgePosition = { x: x + tagOffset.x + 7, y: y + tagOffset.y - 7 };
        if (tintGhostGraphic) {
          let imageBounds = svgPlacement;
          if (isLight && lightImageUrl && lightImageHitRect) {
            const scale = Math.min(svgPlacement.width / lightImageHitRect.imageWidth, svgPlacement.height / lightImageHitRect.imageHeight);
            imageBounds = { ...svgPlacement,
              x: svgPlacement.x + (svgPlacement.width - lightImageHitRect.imageWidth * scale) / 2 + lightImageHitRect.x * scale,
              y: svgPlacement.y + (svgPlacement.height - lightImageHitRect.imageHeight * scale) / 2 + lightImageHitRect.y * scale,
              width: lightImageHitRect.width * scale, height: lightImageHitRect.height * scale };
          }
          addBadgePosition = planGraphicBadgePosition({ x, y }, svgPlacement, imageBounds);
        }

        let developerInfo: JSX.Element | null = null;

        if (isUiModeDeveloper()) {
          developerInfo = (
            <g
              data-c1-export-exclude=""
              key={item.id}
              data-c1-equipment-kind="eloItem"
              data-c1-equipment-id={item.id}
              data-c1-definition-id={item.definition?.definitionId ?? ""}
              data-c1-opening-side={openingEloSide(item)}
            >
              <text
                x={labelX}
                y={labelY}
                fill="#111827"
                fontSize={5.6}
                textAnchor={isGroup ? "end" : "start"}
                dominantBaseline="central"
                transform={`rotate(${textRotation} ${labelX} ${labelY})`}
              >
                {item.definition?.definitionId}
              </text>
            </g>
          );
        }

        if (isGroup && !compactPlugGroup && !groupIcon) return developerInfo;

        return (
          <g key={`${item.id}:${suffix}`}>
          <g transform={eloItemScaleTransform(x, y, (c1_state.floorImageRenderOptions.value?.eloItemScale ?? c1_state.settings.eloItemScale), isGhost && hoveredItemId !== item.id ? 0.5 : 1)}
            opacity={ownerSelected && independentOwner ? eloSelectionOpacity(independentOwner, c1_state) : 1}
            {...(independentOwner ? eloSelectionHoverHandlers(independentOwner, c1_state) : {})}>
          <ConnectionEndpointGroup
            object={isGhost || suffix ? undefined : item.owner}
            data-connection-anchor={isLight && !technicalMode ? "center" : undefined}
            data-connection-channel-anchors={isButton && !technicalMode ? "" : undefined}
            anchorSelector={isButton ? "[data-c1-button-connection-anchor]" : item instanceof C1_Consumer ? "[data-c1-consumer-connection-anchor]" : undefined}
            key={item.id}
            focusable="false"
            className={`C1_svgEloItem${compactPlugGroup ? " C1_compactPlugGroup" : ""}${isSelected && !compactPlugGroup ? " C1_selected" : ""}${parentSelected ? " C1_parentSelected" : ""}${childSelected ? " C1_childSelected" : ""}${isGhost ? " C1_eloGhostItem" : ""}${draggingItemId === item.id ? " C1_dragging" : ""}`}
            data-c1-equipment-kind="eloItem"
            data-c1-equipment-id={item.id}
            data-connection-interaction={electricallyInteractive ? "prefer-control" : undefined}
            data-connection-interaction-plan-object={isGhost ? undefined : item.owner?.id}
            data-c1-definition-id={item.definition?.definitionId ?? ""}
            data-c1-opening-side={openingEloSide(item)}
            pointerEvents={electricallyInteractive ? "visiblePainted" : "none"}
            style={{
              cursor: draggingItemId === item.id ? "move" : isGhost ? "default" : undefined,
            }}
            data-tv-toast={electricallyInteractive && tooltipEnabled && !isGhost ? "" : undefined}
            data-tooltip-background-color={tagBackgroundColor(item)}
            data-tooltip-color={getContrastTextColor(tagBackgroundColor(item))}
            data-tooltip-id={electricallyInteractive && tooltipEnabled && !isGhost ? "tv-tooltip" : undefined}
            data-tooltip-html={electricallyInteractive && tooltipEnabled && !isGhost ? eloItemTooltipHtml(item) : undefined}
            data-tooltip-delay-show={500}
            onPointerEnter={
              electricallyInteractive
                ? (event) => {
                    setHoveredItemId(item.id);
                    if (isGhost) {
                      hideTooltip();
                      return;
                    }
                  }
                : undefined
            }
            onPointerLeave={() => {
              hideTooltip();
              setHoveredItemId((current) => (current === item.id ? undefined : current));
              if (!pendingDrag.current?.dragging && draggingItemId === item.id) setDraggingItemId(undefined);
            }}
            onPointerDown={
              electricallyInteractive
                ? (event) => {
                    if (ghostEntry) {
                      event.preventDefault();
                      event.stopPropagation();
                      if (event.button !== 0 || !item.evaluatedAbsolutePosition) return;
                      const canvas = event.currentTarget.closest(".C1_canvas");
                      if (!canvas) return;
                      const pointer = clientToPlan(canvas, event.clientX, event.clientY, c1_state.view);
                      if (!pointer) return;
                      const instantiatedItem = applyGhostAction(
                        ghostEntry.action,
                        item.definition?.definitionId,
                        true,
                      );
                      if (!instantiatedItem) return;
                      ghostDrag.current = {
                        item: instantiatedItem,
                        pointerId: event.pointerId,
                        canvas,
                        startClient: { x: event.clientX, y: event.clientY },
                        grabOffset: {
                          x: pointer.x - item.evaluatedAbsolutePosition.x,
                          y: pointer.y - item.evaluatedAbsolutePosition.y,
                        },
                        dragging: false,
                      };
                      return;
                    }
                    // Mobile selects on release; desktop retains the click/double-click path below.
                    if (!compactPlugGroup) startMobileTap(event, item);
                    event.stopPropagation();
                    if (eloItemsMovable) startPotentialDrag(event, item, plugGroupForItem(item) ?? item);
                  }
                : undefined
            }
            onPointerMove={eloItemsMovable && !isGhost ? event => { moveMobileTap(event); movePotentialDrag(event); } : undefined}
            onPointerUp={eloItemsMovable && !isGhost ? event => finishMobileEloPointer(event, item.id) : undefined}
            onPointerCancel={eloItemsMovable && !isGhost ? event => finishMobileEloPointer(event, item.id) : undefined}
            onClick={
              electricallyInteractive
                ? (event) => {
                    event.stopPropagation();
                    hideTooltip();
                    if (ghostEntry) {
                      event.preventDefault();
                      applyGhostAction(ghostEntry.action);
                      return;
                    }
                    if (suppressedClickItemId.current === item.id) {
                      suppressedClickItemId.current = undefined;
                      event.preventDefault();
                      return;
                    }
                    // Kompakte Steckdosengruppen sind reine Container. Die
                    // Auswahl erfolgt ausschlieÃŸlich Ã¼ber ihre SteckplÃ¤tze.
                    if (compactPlugGroup) return;
                    delayedClick.schedule(() => {
                      if (item.owner?.isDeleted) return;
                      onClick?.(item);
                      c1_state.setSelection(item);
                      c1_state.eventLogger.addEvent(C1_EventKey.EloItemDetailsSelected, {
                        eloItemId: item.id,
                        definitionId: item.definition?.definitionId ?? "",
                      });
                      state.dockManager.handleItemSelection(item);
                    }, event.detail);
                  }
                : undefined
            }
            onDoubleClick={
              electricallyInteractive
                ? (event) => {
                    delayedClick.cancel();
                    if (!onDoubleClick) return;
                    event.stopPropagation();
                    onDoubleClick(item);
                  }
                : undefined
            }
          >
            <g filter={tintGhostGraphic && !showEquipmentDesignView ? `url(#${ghostImageFilterId})` : undefined} opacity={tintGhostGraphic ? 0.92 : 1}>
            {dinMode && !isGroup && !(renderingPdf && isButton) ? (
              <g className="C1_dinPresentation" transform={`translate(${tagOffset.x} ${tagOffset.y})`}>
                <rect className="C1_svgEloItemCircle" data-c1-consumer-connection-anchor=""
                  data-c1-button-connection-anchor={isButton ? "" : undefined}
                  x={x - C1_TAG_RADIUS} y={y - C1_TAG_RADIUS} width={C1_TAG_RADIUS * 2} height={C1_TAG_RADIUS * 2}
                  rx={2} fill={isSelected ? "#fef3c7" : "#fff"} fillOpacity={0.85}
                  stroke={isSelected ? "#f59e0b" : indirectlySelected ? "#fdba74" : "none"}
                  strokeWidth={1} pointerEvents="all" />
                <C1_DinSymbol symbolType={definition?.type} details={{...definition?.defaultDetails, ...item.data.details}}
                  x={x - 9} y={y - 9} width={18} height={18}
                  color={isGhost ? "#16a34a" : "#173f56"} pointerEvents="none" />
                {/* {showButtonPin && <C1_ButtonPinBadge x={x - C1_TAG_RADIUS} y={y - C1_TAG_RADIUS} />} */}
              </g>
            ) : designOutlet ? (
              <g
                transform={svgPlacement.transform}
                filter={isSelected && !compactPlugGroup ? imageSelectionFilter : undefined}
              >
                <rect
                  data-c1-consumer-connection-anchor=""
                  x={svgPlacement.x}
                  y={svgPlacement.y}
                  width={svgPlacement.width}
                  height={svgPlacement.height}
                  fill="none"
                  pointerEvents="none"
                />
                <g {...vectorContrastProps}>
                  <WallOutletSvg
                    slots={outletSlots}
                    slotLabels={wallOutletItems(item).map((child) =>
                      switchedCircuitLabel(child.data.details?.plugType),
                    )}
                    extraSlot={showCompactPlugGroupAdd}
                    selected={compactPlugGroup ? false : isSelected}
                    indirectlySelected={indirectlySelected}
                    contentRotation={svgPlacement.rotation}
                    selectedSlotFilter={`url(#${selectionImageFilterId})`}
                    hoveredSlotIndex={wallOutletItems(item).findIndex((child) => child.id === hoveredOutletChildId)}
                    selectedSlotIndex={
                      selectedChild
                        ? wallOutletItems(item).findIndex((child) => child.id === selectedChild.id)
                        : undefined
                    }
                    x={svgPlacement.x}
                    y={svgPlacement.y}
                    width={svgPlacement.width}
                    height={svgPlacement.height}
                  />
                </g>
                {compactPlugGroup &&
                  item instanceof C1_EloGroup &&
                  !isGhost &&
                  definition &&
                  electricallyInteractive &&
                  c1_state.floorImageRenderOptions.value === null && (
                  <C1_EloInlineEditor
                    item={item}
                    definition={definition}
                    groupSelected
                    interactionOnly={c1_state.editMode.value === "interactionConcept"}
                    selectedChildId={selectedChild?.id}
                    showAdd={Boolean(selectedChild)}
                    onSlotHoverChange={setHoveredOutletChildId}
                    onAddHover={hideTooltip}
                    onSlotPointerDown={(event, child) => {
                      if (!eloItemsMovable) return;
                      startMobileTap(event, child);
                      startPotentialDrag(event, child, item);
                    }}
                    onSlotClick={(child) => {
                      if (suppressedClickItemId.current === item.id) {
                        suppressedClickItemId.current = undefined;
                        return;
                      }
                      hideTooltip();
                      delayedClick.schedule(() => {
                        if (child.owner?.isDeleted) return;
                        onClick?.(child);
                        c1_state.setSelection(child);
                        c1_state.eventLogger.addEvent(C1_EventKey.EloItemDetailsSelected, {
                          eloItemId: child.id,
                          definitionId: child.definition?.definitionId ?? "",
                        });
                      });
                    }}
                    contentRotation={svgPlacement.rotation}
                    geometry={{
                      x: svgPlacement.x,
                      y: svgPlacement.y,
                      width: svgPlacement.width,
                      height: svgPlacement.height,
                    }}
                  />
                )}
              </g>
            ) : isCamera && !technicalMode ? (
              <g transform={svgPlacement.transform} filter={isSelected || indirectlySelected ? imageSelectionFilter : undefined}>
                <g {...vectorContrastProps}>
                  <CameraFrontView x={svgPlacement.x} y={svgPlacement.y} width={svgPlacement.width} height={svgPlacement.height} />
                </g>
                <rect data-c1-consumer-connection-anchor=""
                  x={svgPlacement.x} y={svgPlacement.y} width={svgPlacement.width} height={svgPlacement.height}
                  fill="transparent" pointerEvents="all" />
              </g>
            ) : isFixedPower && !technicalMode ? (
              <g
                transform={svgPlacement.transform}
                color="#173f56"
                filter={isSelected || indirectlySelected ? imageSelectionFilter : undefined}
              >
                <g
                  transform={`translate(${svgPlacement.x + FIXED_POWER_SYMBOL_SIZE / 2} ${svgPlacement.y + FIXED_POWER_SYMBOL_SIZE / 2}) scale(${FIXED_POWER_SYMBOL_SCALE})`}
                  {...vectorContrastProps}
                >
                  <FixedPowerFrontView
                    voltage={fixedPowerVoltage ?? "230V"}
                    idPrefix={`c1-fixed-consumer-${item.id}`}
                    selected={isSelected}
                    contentRotation={svgPlacement.rotation}
                    contentReflected={svgPlacement.reflected}
                  />
                </g>
                <rect
                  data-c1-consumer-connection-anchor=""
                  x={svgPlacement.x}
                  y={svgPlacement.y}
                  width={svgPlacement.width}
                  height={svgPlacement.height}
                  fill="transparent"
                  pointerEvents="all"
                />
              </g>
            ) : isButton && item instanceof C1_Button && (!technicalMode || (renderingPdf && dinMode)) ? (
              <>
                <g
                  transform={svgPlacement.transform}
                  filter={imageSelectionFilter}
                >
                  <rect
                    className="C1_svgButtonHitArea"
                    data-c1-button-connection-anchor=""
                    x={svgPlacement.x}
                    y={svgPlacement.y}
                    width={svgPlacement.width}
                    height={svgPlacement.height}
                    fill="transparent"
                    pointerEvents="all"
                  />
                  <g {...vectorContrastProps}>
                    <C1_ButtonView
                      eloItem={item}
                      getButtonChannelId={isGhost ? undefined : (rowIndex, side) => getC1ButtonDevice(item)?.getButtonChannel(rowIndex, side === "right" ? 1 : 0)?.id}
                      selected={isSelected || indirectlySelected}
                      x={svgPlacement.x}
                      y={svgPlacement.y}
                      width={svgPlacement.width}
                      height={svgPlacement.height}
                      usePhysicalSize={false}
                      contentRotation={renderUpright ? svgPlacement.rotation : 0}
                      contentReflected={renderUpright && svgPlacement.reflected}
                      frontImageReflected={svgPlacement.reflected}
                    />
                  </g>
                </g>
                {/* {showButtonPin && <C1_ButtonPinBadge {...planGraphicBadgePosition({ x, y }, svgPlacement, svgPlacement, "left")} />} */}
              </>
            ) : isLight && lightImageUrl && !technicalMode ? (
              <g transform={svgPlacement.transform}>
                {lightOutlineRadiusRatio !== undefined && (
                  <circle
                    className="C1_svgLightOutline"
                    data-connection-cutout="circle"
                    data-c1-consumer-connection-anchor=""
                    cx={svgPlacement.x + svgPlacement.width / 2}
                    cy={svgPlacement.y + svgPlacement.height / 2}
                    r={Math.min(svgPlacement.width, svgPlacement.height) * lightOutlineRadiusRatio}
                    fill={isGhost ? "#16a34a" : isSelected ? "#f59e0b" : indirectlySelected ? "#fdba74" : "#fff"}
                    fillOpacity={isSelected ? 1 : 0.5}
                    stroke={isGhost ? "#16a34a" : isSelected ? "#9a3412" : indirectlySelected ? "#fdba74" : "#94a3b8"}
                    strokeWidth={isSelected ? 1.4 : indirectlySelected ? 1.1 : 0.6}
                    pointerEvents="all"
                  />
                )}
                {lightImageHitRect &&
                  (() => {
                    const scale = Math.min(
                      svgPlacement.width / lightImageHitRect.imageWidth,
                      svgPlacement.height / lightImageHitRect.imageHeight,
                    );
                    const renderedWidth = lightImageHitRect.imageWidth * scale;
                    const renderedHeight = lightImageHitRect.imageHeight * scale;
                    const renderedX = svgPlacement.x + (svgPlacement.width - renderedWidth) / 2;
                    const renderedY = svgPlacement.y + (svgPlacement.height - renderedHeight) / 2;
                    return (
                      <rect
                        className="C1_svgLightHitArea"
                        data-c1-consumer-connection-anchor=""
                        focusable="false"
                        x={renderedX + lightImageHitRect.x * scale}
                        y={renderedY + lightImageHitRect.y * scale}
                        width={lightImageHitRect.width * scale}
                        height={lightImageHitRect.height * scale}
                        fill="transparent"
                        pointerEvents="all"
                      />
                    );
                  })()}
                {!lightImageHitRect && (
                  <rect
                    className="C1_svgLightHitArea"
                    data-c1-consumer-connection-anchor=""
                    focusable="false"
                    x={svgPlacement.x}
                    y={svgPlacement.y}
                    width={svgPlacement.width}
                    height={svgPlacement.height}
                    fill="transparent"
                    pointerEvents="all"
                  />
                )}
                <C1_FurnitureUprightContent
                  anchorX={svgPlacement.x + svgPlacement.width / 2}
                  anchorY={svgPlacement.y + svgPlacement.height / 2}
                  ownerRotation={lightOutlineRadiusRatio !== undefined ? svgPlacement.rotation : 0}
                  ownerReflect={lightOutlineRadiusRatio !== undefined && svgPlacement.reflected}
                >
                  <image
                    className="C1_svgLightInlineImage"
                    focusable="false"
                    href={lightImageUrl}
                    x={svgPlacement.x}
                    y={svgPlacement.y}
                    width={svgPlacement.width}
                    height={svgPlacement.height}
                    preserveAspectRatio="xMidYMid meet"
                    opacity={1}
                    pointerEvents="none"
                    filter={imageSelectionFilter}
                    style={{ userSelect: "none" }}
                  />
                </C1_FurnitureUprightContent>
                {placementLabel || isDaliLight ? (
                  <g className="C1_svgLightPlacementBadge" pointerEvents="visiblePainted">
                    <circle
                      cx={0}
                      cy={0}
                      r={2.8}
                      fill="#ffffff"
                      stroke={isDaliLight ? "#2563eb" : "none"}
                      strokeWidth={isDaliLight ? 0.6 : 0}
                    />
                    {placementLabel && (
                      <text
                        x={0}
                        y={0}
                        fill="#475569"
                        fontSize={placementLabel === "RGB" ? 1.7 : 2}
                        fontWeight={600}
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        {placementLabel}
                      </text>
                    )}
                  </g>
                ) : showSwitchDebugPoint ? (
                  <circle
                    className="C1_svgLightPlacementPoint"
                    cx={0}
                    cy={0}
                    r={1.3}
                    fill="#94a3b8"
                    pointerEvents="none"
                  />
                ) : null}
              </g>
            ) : showEquipmentDesignView ? (
              <g transform={svgPlacement.transform}>
                <circle
                  className="C1_svgSensorOutline"
                  data-connection-cutout="circle"
                  cx={svgPlacement.x + svgPlacement.width / 2}
                  cy={svgPlacement.y + svgPlacement.height / 2}
                  r={Math.min(svgPlacement.width, svgPlacement.height) / 2}
                  fill={isGhost ? "#16a34a" : isSelected ? "#f59e0b" : indirectlySelected ? "#fdba74" : "#fff"}
                  fillOpacity={isSelected ? 1 : 0.5}
                  stroke={isGhost ? "#16a34a" : isSelected ? "#9a3412" : indirectlySelected ? "#fdba74" : "#94a3b8"}
                  strokeWidth={isSelected ? 1.4 : indirectlySelected ? 1.1 : 0.6}
                  pointerEvents="none"
                />
                <rect
                  className="C1_svgSensorHitArea"
                  data-c1-consumer-connection-anchor=""
                  x={svgPlacement.x}
                  y={svgPlacement.y}
                  width={svgPlacement.width}
                  height={svgPlacement.height}
                  fill="transparent"
                  stroke="none"
                  pointerEvents="all"
                />
                <C1_FurnitureUprightContent
                  anchorX={svgPlacement.x + svgPlacement.width / 2}
                  anchorY={svgPlacement.y + svgPlacement.height / 2}
                  ownerRotation={svgPlacement.rotation}
                  ownerReflect={svgPlacement.reflected}
                >
                  {sensorIconTiles(
                    equipmentIcons,
                    svgPlacement.x + svgPlacement.width / 4,
                    svgPlacement.y + svgPlacement.height / 4,
                    svgPlacement.width / 2,
                    svgPlacement.height / 2,
                  ).map((tile, index) => (
                    <image
                      key={`${tile.url}-${index}`}
                      className="C1_svgSensorInlineImage"
                      href={tile.url}
                      x={tile.x}
                      y={tile.y}
                      width={tile.width}
                      height={tile.height}
                      preserveAspectRatio="xMidYMid meet"
                      pointerEvents="none"
                      filter={isGhost ? `url(#${ghostImageFilterId})` : imageSelectionFilter}
                    />
                  ))}
                </C1_FurnitureUprightContent>
              </g>
            ) : (
              <g className="C1_tagPresentation" transform={`translate(${tagOffset.x} ${tagOffset.y})`}>
                <C1_TagShape
                  x={x}
                  y={y}
                  isButton={isButton && !renderingPdf}
                  withSymbol={!!consumerSymbol}
                  fill={isGhost ? "green" : isSelected ? "#f59e0b" : tagBackgroundColor(item)}
                  stroke={isGhost ? "#ffffff" : isSelected ? "none" : indirectlySelected ? "#fdba74" : "rgba(255, 255, 255, 0.5)"}
                  strokeWidth={indirectlySelected && !isSelected ? 2.2 : 1.5}
                />
                {consumerSymbol && (
                  <image
                    href={consumerSymbol}
                    x={x - 5.5}
                    y={y - 11}
                    width={11}
                    height={11}
                    preserveAspectRatio="xMidYMid meet"
                    pointerEvents="none"
                  />
                )}
                {compactDeviceIcon && (
                  <image
                    href={compactDeviceIcon}
                    x={x - 4}
                    y={y - (definition?.type === C1_EloItem_Type.IntermediateDistribution ? 8.5 : 6.9)}
                    width={8}
                    height={6}
                    preserveAspectRatio="none"
                    pointerEvents="none"
                  />
                )}
                {sensorIconTiles(ghostIcons, x - 4, y - 6.9, 8, 6).map((tile) => (
                  <image key={tile.url} href={tile.url} x={tile.x} y={tile.y}
                    width={tile.width} height={tile.height} preserveAspectRatio="xMidYMid meet"
                    pointerEvents="none" style={{ filter: "brightness(0) invert(1)" }} />
                ))}
                {ghostFunctionLabel && (
                  <C1_TagFunction x={x} y={y - 3.9} label={ghostFunctionLabel} color="#ffffff" />
                )}
                {compactLightTag && (
                  <C1_LightTagFunction
                    x={x}
                    y={y - 2.8}
                    channelType={channelType}
                    isDali={isDaliLight}
                    badgeColor={isSelected ? "#f8bb54" : "#69f0bc"}
                    color={(isGhost ? "#ffffff" : getContrastTextColor(isSelected ? "#f59e0b" : tagBackgroundColor(item)))}
                  />
                )}
                {compactFixedPowerTag && (
                  <C1_TagFunction
                    x={x}
                    y={y - 3.9}
                    label="230"
                    framed
                    color={isGhost ? "#ffffff" : getContrastTextColor(isSelected ? "#f59e0b" : tagBackgroundColor(item))}
                  />
                )}
                {hasPlugFunction && (
                  <C1_PlugTagFunction
                    x={x}
                    y={y - 3.9}
                    plugType={plugType}
                    color={(isGhost ? "#ffffff" : getContrastTextColor(isSelected ? "#f59e0b" : tagBackgroundColor(item)))}
                  />
                )}
                {compactWindowContactTag && (
                  <C1_TagFunction
                    x={x}
                    y={y - 3.9}
                    symbol="contact"
                    color="#ffffff"
                  />
                )}
                <text
                  x={x}
                  y={consumerSymbol ? y + 7 : hasTagFunction ? y + 0.9 : y}
                  fontFamily="Inter, Arial, sans-serif"
                  alignmentBaseline="central"
                  fill={isWindowContact || definition?.type === C1_EloItem_Type.Sensor || definition?.type === C1_EloItem_Type.Thermostat || definition?.type === C1_EloItem_Type.Camera
                    ? "#ffffff"
                    : (isGhost ? "#ffffff" : getContrastTextColor(isSelected ? "#f59e0b" : tagBackgroundColor(item)))}
                  fontSize={tagFontSize}
                  fontWeight={600}
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {displayedTag}
                </text>
                {/* {showButtonPin && <C1_ButtonPinBadge x={x - C1_TAG_RADIUS} y={y - C1_TAG_RADIUS} />} */}
                {isButton &&
                  item instanceof C1_Button &&
                  technicalMode &&
                  !renderingPdf &&
                  !selectedForDisplay && (
                  <C1_ButtonView
                    eloItem={item}
                    className="C1_svgButtonHoverPreview"
                    x={x - 22}
                    y={y - 57}
                    width={44}
                    height={44}
                    usePhysicalSize={false}
                  />
                )}
                {isLight && technicalMode && lightImageUrl && c1_state.lightEditDialogItemId.value !== item.id && (
                  <image
                    className="C1_svgLightHoverPreview"
                    href={lightImageUrl}
                    x={x - 22}
                    y={y - 57}
                    width={44}
                    height={44}
                    preserveAspectRatio="xMidYMid meet"
                    filter={imageSelectionFilter}
                  />
                )}
                {isFixedPower && technicalMode && (
                  <g
                    className="C1_svgFixedPowerHoverPreview"
                    transform={`translate(${x} ${y - 37})`}
                    pointerEvents="none"
                    {...vectorContrastProps}
                  >
                    <FixedPowerFrontView
                      voltage={fixedPowerVoltage ?? "230V"}
                      idPrefix={`c1-fixed-consumer-hover-${item.id}`}
                    />
                  </g>
                )}
                {isPlug && technicalMode && hoverOutletSlots.length > 0 && (
                  <g {...vectorContrastProps}>
                    <WallOutletSvg
                      className="C1_svgOutletHoverPreview"
                      slots={hoverOutletSlots}
                      selectedSlotIndex={hoveredOutletIndex}
                      x={x - hoverOutletWidth / 2}
                      y={y - 57}
                      width={hoverOutletWidth}
                      height={44}
                    />
                  </g>
                )}
                {technicalMode && equipmentIcons.length > 0 && (
                  <g className="C1_svgSensorHoverPreview" pointerEvents="none">
                    {sensorIconTiles(equipmentIcons, x - 11, y - 33, 22, 22).map((tile, index) => (
                      <image
                        key={`${tile.url}-${index}`}
                        href={tile.url}
                        x={tile.x}
                        y={tile.y}
                        width={tile.width}
                        height={tile.height}
                        preserveAspectRatio="xMidYMid meet"
                      />
                    ))}
                  </g>
                )}
              </g>
            )}
            </g>
            {ghostEntry && hoveredItemId === item.id && (
              <g className="C1_eloGhostAddBadge" transform={`translate(${addBadgePosition.x} ${addBadgePosition.y})`} pointerEvents={electricallyInteractive ? "all" : "none"} style={{ cursor: "pointer" }}>
                <C1_EloAddBadge name={
                  (ghostEntry.action.type === "append" && !ghostEntry.action.definition.requiresFurnitureProperty
                    ? ghostEntry.action.definition : definition)
                    ?.description?.[c1_state.language]
                  || (definition ? C1_ELO_ITEM_TYPE_LABELS[definition.type] : "Item")
                } />
              </g>
            )}
            {/* Taster-Edit-Button im Floorplan vorläufig deaktiviert.
            {isButton && item instanceof C1_Button && isSelected && electricallyInteractive &&
              (c1_state.editMode.value === "EloDetail" || c1_state.editMode.value === "interactionConcept") && (() => {
                const position = technicalMode
                  ? { x: x + tagOffset.x + C1_TAG_RADIUS, y: y + tagOffset.y - C1_TAG_RADIUS }
                  : planGraphicBadgePosition({ x, y }, svgPlacement);
                // Keep the control in screen pixels while its anchor follows the symbol.
                const screenScale = c1_state.svgPx / c1_state.px / normalizeEloItemScale((c1_state.floorImageRenderOptions.value?.eloItemScale ?? c1_state.settings.eloItemScale));
                const editorOpen = state.dockManager.buttonEditor.content.value === item &&
                  state.dockManager.buttonEditor.visibility.value !== "hidden";
                const toggleEditor = () => {
                  delayedClick.cancel();
                  if (editorOpen) state.dockManager.closeButtonEditor();
                  else {
                    c1_state.setSelection(item);
                    state.dockManager.openButtonEditor(item, true);
                  }
                };
                return <C1_PlanControlPortal transform={`translate(${position.x} ${position.y}) scale(${screenScale})`}>
                  <g className="C1_planButtonEdit" data-testid="c1-button-edit" role="button" tabIndex={0}
                  aria-label={itx("c1.ui.Button_bearbeiten", "Button bearbeiten")}
                  aria-pressed={editorOpen}
                  style={{ cursor: "pointer" }}
                  onPointerDown={event => event.stopPropagation()}
                  onDoubleClick={event => event.stopPropagation()}
                  onClick={event => { event.stopPropagation(); toggleEditor(); }}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault(); event.stopPropagation(); toggleEditor();
                    }
                  }}>
                  <title>{itx("c1.ui.Button_bearbeiten", "Button bearbeiten")}</title>
                  <rect x={0} y={-24} width={40} height={24} rx={12} fill={editorOpen ? "#f59e0b" : "white"} stroke="#64748b" strokeWidth={1} />
                  <foreignObject x={0} y={-24} width={40} height={24} pointerEvents="none">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#334155", fontSize: 18 }}>
                      <i className="las la-edit" aria-hidden="true" />
                    </div>
                  </foreignObject>
                </g>
                </C1_PlanControlPortal>;
              })()}
            */}
            {developerInfo}
          </ConnectionEndpointGroup>
          </g>
          {developerMode && !renderingPdf && !isGhost && <C1_EloCollisionFootprint
            itemId={`${item.id}:${suffix}`}
            footprint={displayFootprint(item, position, {
              eloItem, origin, ownerSelected, rotation, exteriorLightRotation, reflect, wallDockSide, uprightItemTypes,
            }, displayMode)}
            view={view}
            px={PX}
          />}
          </g>
        );
      })}
    </g>
  );
}
